/**
 * Agent Detection
 *
 * Detects which AI coding agents are installed by checking:
 * - Config directories (e.g., ~/.claude/, ~/.cursor/)
 * - Binaries in PATH
 *
 * Also checks if Sailfish skill/MCP are already installed for each agent.
 */

import { existsSync, readFileSync } from "fs";
import { homedir } from "os";
import { join } from "path";
import { execSync } from "child_process";

export interface DetectedAgent {
  id: string;
  name: string;
  detected: boolean;
  hasSkill: boolean;
  hasMcp: boolean;
}

interface AgentProbe {
  id: string;
  name: string;
  detect: () => boolean;
  checkSkill: () => boolean;
  checkMcp: () => boolean;
}

const home = homedir();

function isInPath(cmd: string): boolean {
  try {
    execSync(`which ${cmd}`, { stdio: "pipe" });
    return true;
  } catch {
    return false;
  }
}

function dirExists(p: string): boolean {
  return existsSync(p);
}

const SKILL_DIR = "sailfish-autofix";

const agentProbes: AgentProbe[] = [
  {
    id: "claude-code",
    name: "Claude Code",
    detect: () => dirExists(join(home, ".claude")),
    checkSkill: () =>
      dirExists(join(home, ".claude", "skills", SKILL_DIR)) ||
      dirExists(join(home, ".agents", "skills", SKILL_DIR)),
    checkMcp: () => {
      try {
        const config = JSON.parse(
          readFileSync(join(home, ".claude.json"), "utf-8")
        );
        return !!config.mcpServers?.sailfishAutofix;
      } catch {
        return false;
      }
    },
  },
  {
    id: "cursor",
    name: "Cursor",
    detect: () =>
      dirExists(join(home, ".cursor")) ||
      dirExists(join(home, "Library", "Application Support", "Cursor")),
    checkSkill: () =>
      dirExists(join(home, ".cursor", "skills", SKILL_DIR)) ||
      dirExists(join(home, ".agents", "skills", SKILL_DIR)),
    checkMcp: () => {
      // Cursor uses project-level .cursor/mcp.json or global config
      try {
        const config = JSON.parse(
          readFileSync(join(home, ".cursor", "mcp.json"), "utf-8")
        );
        return !!config.mcpServers?.sailfishAutofix;
      } catch {
        return false;
      }
    },
  },
  {
    id: "codex",
    name: "Codex (OpenAI)",
    detect: () => dirExists(join(home, ".codex")) || isInPath("codex"),
    checkSkill: () =>
      dirExists(join(home, ".codex", "skills", SKILL_DIR)) ||
      dirExists(join(home, ".agents", "skills", SKILL_DIR)),
    checkMcp: () => {
      try {
        const content = readFileSync(
          join(home, ".codex", "config.toml"),
          "utf-8"
        );
        return content.includes("sailfishAutofix") || content.includes("sailfish-autofix");
      } catch {
        return false;
      }
    },
  },
  {
    id: "copilot",
    name: "GitHub Copilot",
    detect: () =>
      // Check for VS Code with Copilot extension or Copilot CLI config
      dirExists(join(home, ".vscode", "extensions")) &&
        existsSync(join(home, ".vscode", "mcp.json")) ||
      isInPath("github-copilot"),
    checkSkill: () => dirExists(join(home, ".agents", "skills", SKILL_DIR)),
    checkMcp: () => {
      try {
        const config = JSON.parse(
          readFileSync(join(home, ".vscode", "mcp.json"), "utf-8")
        );
        return !!config.servers?.sailfishAutofix;
      } catch {
        return false;
      }
    },
  },
  {
    id: "opencode",
    name: "OpenCode",
    detect: () => isInPath("opencode"),
    checkSkill: () => dirExists(join(home, ".agents", "skills", SKILL_DIR)),
    checkMcp: () => {
      try {
        const config = JSON.parse(
          readFileSync(join(home, ".config", "opencode", "config.json"), "utf-8")
        );
        return !!config.mcpServers?.sailfishAutofix;
      } catch {
        return false;
      }
    },
  },
  {
    id: "windsurf",
    name: "Windsurf",
    detect: () =>
      dirExists(join(home, ".codeium", "windsurf")) ||
      dirExists(join(home, ".windsurf")),
    checkSkill: () =>
      dirExists(join(home, ".windsurf", "skills", SKILL_DIR)) ||
      dirExists(join(home, ".agents", "skills", SKILL_DIR)),
    checkMcp: () => {
      try {
        const config = JSON.parse(
          readFileSync(join(home, ".windsurf", "mcp.json"), "utf-8")
        );
        return !!config.mcpServers?.sailfishAutofix;
      } catch {
        return false;
      }
    },
  },
  {
    id: "gemini",
    name: "Gemini CLI",
    detect: () => isInPath("gemini") || dirExists(join(home, ".gemini")),
    checkSkill: () =>
      dirExists(join(home, ".gemini", "skills", SKILL_DIR)) ||
      dirExists(join(home, ".agents", "skills", SKILL_DIR)),
    checkMcp: () => {
      try {
        const config = JSON.parse(
          readFileSync(join(home, ".gemini", "settings.json"), "utf-8")
        );
        return !!config.mcpServers?.sailfishAutofix;
      } catch {
        return false;
      }
    },
  },
  {
    id: "goose",
    name: "Goose",
    detect: () => isInPath("goose") || dirExists(join(home, ".config", "goose")),
    checkSkill: () => dirExists(join(home, ".agents", "skills", SKILL_DIR)),
    checkMcp: () => {
      try {
        const content = readFileSync(
          join(home, ".config", "goose", "config.yaml"),
          "utf-8"
        );
        return content.includes("sailfishAutofix") || content.includes("sailfish-autofix");
      } catch {
        return false;
      }
    },
  },
];

/**
 * Probe every supported agent, including ones not installed on this machine.
 */
export function detectAllAgents(): DetectedAgent[] {
  return agentProbes.map((probe) => ({
    id: probe.id,
    name: probe.name,
    detected: probe.detect(),
    hasSkill: probe.checkSkill(),
    hasMcp: probe.checkMcp(),
  }));
}

/**
 * Detect all installed agents and their Sailfish installation status.
 */
export function detectAgents(): DetectedAgent[] {
  return detectAllAgents().filter((a) => a.detected);
}

/**
 * Pick the agents doctor should report on.
 *
 * Doctor diagnoses what the user opted into, not everything installed on the
 * machine. Scope to the agent ids recorded at setup time when present; for
 * installs predating that record, fall back to agents that have Sailfish MCP
 * config written — the only per-agent footprint setup leaves behind (the
 * skill lives in a shared dir, so its presence says nothing about one agent).
 */
export function selectDoctorAgents(
  all: DetectedAgent[],
  configuredIds: string[] | undefined
): DetectedAgent[] {
  if (configuredIds && configuredIds.length > 0) {
    const wanted = new Set(configuredIds);
    return all.filter((a) => wanted.has(a.id));
  }
  return all.filter((a) => a.detected && a.hasMcp);
}

/**
 * Get all supported agent IDs.
 */
export function getAllAgentIds(): string[] {
  return agentProbes.map((p) => p.id);
}
